// apps/client/src/hooks/useCookieConsent.ts
// Cookie consent management hook with localStorage persistence and Firestore audit trail

import { useState, useEffect, useCallback, useMemo } from 'react';
import { doc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { posthog } from '../providers/PostHogProvider';
import { useAuth } from './useAuth';

const CONSENT_KEY = 'valutten_consent';
const POLICY_VERSION = '1.0';

interface ConsentState {
  /** PostHog analytics tracking */
  analytics: boolean;
  /** Anthropic AI feature processing */
  aiProcessing: boolean;
  /** Shorthand: at least one consent category granted */
  granted: boolean;
  /** Consent state has been loaded from storage */
  loaded: boolean;
}

interface StoredConsent {
  analytics: boolean;
  aiProcessing: boolean;
  policyVersion: string;
  updatedAt: string;
}

export interface UseConsentReturn {
  consent: ConsentState;
  grantAnalytics: () => void;
  revokeAnalytics: () => void;
  grantAI: () => void;
  revokeAI: () => void;
  acceptAll: () => void;
  declineAll: () => void;
  /** User has interacted with the consent banner (localStorage key exists) */
  hasResponded: boolean;
}

function readStoredConsent(): StoredConsent | null {
  try {
    const raw = localStorage.getItem(CONSENT_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as StoredConsent;
  } catch {
    return null;
  }
}

function writeStoredConsent(analytics: boolean, aiProcessing: boolean): void {
  const data: StoredConsent = {
    analytics,
    aiProcessing,
    policyVersion: POLICY_VERSION,
    updatedAt: new Date().toISOString(),
  };
  localStorage.setItem(CONSENT_KEY, JSON.stringify(data));
}

export function useCookieConsent(): UseConsentReturn {
  const { user } = useAuth();
  const [analytics, setAnalytics] = useState(false);
  const [aiProcessing, setAiProcessing] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [hasResponded, setHasResponded] = useState(false);

  // Load consent from localStorage on mount
  useEffect(() => {
    const stored = readStoredConsent();
    if (stored) {
      setAnalytics(stored.analytics);
      setAiProcessing(stored.aiProcessing);
      setHasResponded(true);
    }
    setLoaded(true);
  }, []);

  // Persist consent record to Firestore for audit trail
  const persistToFirestore = useCallback(
    async (analyticsVal: boolean, aiVal: boolean) => {
      if (!user?.uid) return;
      try {
        const consentRef = doc(db, 'users', user.uid, 'consent_records', 'current');
        await setDoc(consentRef, {
          analytics: analyticsVal,
          aiProcessing: aiVal,
          policyVersion: POLICY_VERSION,
          updatedAt: serverTimestamp(),
          userAgent: navigator.userAgent,
        });
      } catch {
        // Non-blocking: consent still works via localStorage even if Firestore write fails
      }
    },
    [user?.uid]
  );

  // Apply PostHog opt-in/opt-out when analytics consent changes
  const applyPostHogConsent = useCallback((granted: boolean) => {
    try {
      if (granted) {
        posthog?.opt_in_capturing();
      } else {
        posthog?.opt_out_capturing();
      }
    } catch {
      // PostHog may not be initialized in dev
    }
  }, []);

  const updateConsent = useCallback(
    (nextAnalytics: boolean, nextAi: boolean) => {
      setAnalytics(nextAnalytics);
      setAiProcessing(nextAi);
      setHasResponded(true);
      writeStoredConsent(nextAnalytics, nextAi);
      applyPostHogConsent(nextAnalytics);
      persistToFirestore(nextAnalytics, nextAi);
    },
    [applyPostHogConsent, persistToFirestore]
  );

  const grantAnalytics = useCallback(() => {
    updateConsent(true, aiProcessing);
  }, [updateConsent, aiProcessing]);

  const revokeAnalytics = useCallback(() => {
    updateConsent(false, aiProcessing);
  }, [updateConsent, aiProcessing]);

  const grantAI = useCallback(() => {
    updateConsent(analytics, true);
  }, [updateConsent, analytics]);

  const revokeAI = useCallback(() => {
    updateConsent(analytics, false);
  }, [updateConsent, analytics]);

  const acceptAll = useCallback(() => {
    updateConsent(true, true);
  }, [updateConsent]);

  const declineAll = useCallback(() => {
    updateConsent(false, false);
  }, [updateConsent]);

  const consent = useMemo<ConsentState>(
    () => ({
      analytics,
      aiProcessing,
      granted: analytics || aiProcessing,
      loaded,
    }),
    [analytics, aiProcessing, loaded]
  );

  return {
    consent,
    grantAnalytics,
    revokeAnalytics,
    grantAI,
    revokeAI,
    acceptAll,
    declineAll,
    hasResponded,
  };
}
